import { useState, type InputHTMLAttributes } from 'react'
import { Eye, EyeOff } from 'lucide-react'

type PasswordInputProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'className'> & {
  /** The input's own classes, margins included: a margin moves to the wrapper, so the eye is centred on the field and not on the field plus its margin. */
  className?: string
}

const MARGIN_CLASS = /^-?m[tby]?-/

/**
 * A password field with an eye at its right edge that shows the password
 * as typed, and hides it again, for the person setting or entering it -
 * every sign-in, reset and invitation page uses it. The eye is an icon on
 * its own, not a boxed button, so it keeps its own look; the field is
 * hidden until the person asks to see it.
 */
export function PasswordInput({ className = '', ...inputProps }: PasswordInputProps) {
  const [shown, setShown] = useState(false)
  const Icon = shown ? EyeOff : Eye
  const classes = className.split(/\s+/).filter((name) => name !== '')
  const margins = classes.filter((name) => MARGIN_CLASS.test(name)).join(' ')
  const own = classes.filter((name) => !MARGIN_CLASS.test(name)).join(' ')
  return (
    <div className={`relative ${margins}`.trim()}>
      <input {...inputProps} type={shown ? 'text' : 'password'} className={`${own} pr-10`} />
      <button
        type="button"
        onClick={() => setShown((value) => !value)}
        aria-label={shown ? 'Hide password' : 'Show password'}
        aria-pressed={shown}
        title={shown ? 'Hide password' : 'Show password'}
        className="absolute inset-y-0 right-2 flex items-center p-1 text-ink-mid hover:text-marine focus:outline-none focus-visible:ring-2 focus-visible:ring-marine/40"
      >
        <Icon size={16} aria-hidden="true" />
      </button>
    </div>
  )
}
